import { usePlanReplacementsByProvider } from '@/lib/hooks'
import { SkeletonLine } from '@/shared/components/Skeleton'
import { Tooltip } from '@/shared/components/Tooltip'

/**
 * "Forced replacements across workspaces" — the org-wide rollup of which providers force a
 * delete+create replacement, and in how many workspaces. Sits above the per-workspace providers
 * table in the Providers tab. Silent (renders nothing) when no replacements are correlated so it
 * never adds noise to a clean org.
 */
export function ReplacementsByProviderCallout({ orgId }: { orgId: string }) {
  const { data, loading } = usePlanReplacementsByProvider(orgId)
  const rows = (data ?? []).filter((r) => r.replaced_resource_count > 0)

  if (loading) return <div style={{ padding: '0 24px 16px' }}><SkeletonLine height={20} /></div>
  if (rows.length === 0) return null

  return (
    <div style={{ padding: '0 24px 8px' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
        <span className="badge badge-amber" style={{ fontSize: '0.6rem' }}>⟳ forced replacements</span>
        <span style={{ fontSize: '0.78rem', color: 'var(--text2)' }}>
          Providers forcing a delete+create replacement across this org's recent plans.
        </span>
      </div>
      <div className="table-wrap fu">
        <table>
          <thead>
            <tr>
              <th>
                <Tooltip text="Provider forcing delete+create replacements in recent plans." placement="top">
                  <span tabIndex={0} className="cursor-help">Provider</span>
                </Tooltip>
              </th>
              <th>
                <Tooltip text="Resources this provider forces to be replaced." placement="top">
                  <span tabIndex={0} className="cursor-help">Replaced resources</span>
                </Tooltip>
              </th>
              <th>
                <Tooltip text="Workspaces with a forced replacement. Hover the cell for which ones." placement="top">
                  <span tabIndex={0} className="cursor-help">Workspaces forced</span>
                </Tooltip>
              </th>
              <th>
                <Tooltip text="Workspaces that use this provider at all (lock-file blast radius)." placement="top">
                  <span tabIndex={0} className="cursor-help">Blast radius</span>
                </Tooltip>
              </th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.provider}>
                <td style={{ fontFamily: 'var(--mono)', fontSize: '0.78rem', color: 'var(--text)' }}>{r.provider}</td>
                <td style={{ fontFamily: 'var(--mono)', fontSize: '0.78rem', color: 'var(--amber)' }}>{r.replaced_resource_count}</td>
                <td style={{ fontSize: '0.78rem', color: 'var(--text2)' }}>
                  <Tooltip text={r.workspaces.map((w) => `${w.name} (${w.replaced_resource_count})`).join(', ')} placement="top">
                    <span>
                      forces replace in <strong style={{ color: 'var(--text)' }}>{r.workspaces.length}</strong> workspace{r.workspaces.length !== 1 ? 's' : ''}
                    </span>
                  </Tooltip>
                </td>
                <td style={{ fontFamily: 'var(--mono)', fontSize: '0.75rem', color: 'var(--text3)' }}>
                  {r.blast_radius_workspace_count > 0 ? `${r.blast_radius_workspace_count} using` : '—'}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div style={{ fontFamily: 'var(--mono)', fontSize: '0.6rem', color: 'var(--text3)', margin: '8px 0 16px' }}>
        // Correlated from redacted plan findings — provider + workspace + counts only, never a value.
      </div>
    </div>
  )
}
